
import {
    createApp, defineAsyncComponent, type Component
} from 'vue';
import App from './App.vue';
import { router } from './routes';
import { loggingOptions } from './middleware/logging';
import {
    getDynamicImport,
    getHashMap,
    stylesUrl
} from './loader';

import VueLogger from 'vuejs3-logger';
import { LicenseManager } from '@ag-grid-enterprise/core';

// Common plugins
import { LoggedInUserStore } from '@agyletime/vue-3-common/src/plugins/loggedInUser';
import { FeatureFlowPlugin } from '@agyletime/vue-3-common/src/plugins/featureflow';
import { TooltipPlugin } from '@agyletime/vue-3-common/src/plugins/tooltip';
import { ACLPlugin } from '@agyletime/vue-3-common/src/plugins/acl';
import { TranslationsPlugin } from '@agyletime/vue-3-common/src/plugins/translations';
import { Pendo } from '@agyletime/vue-3-common/src/plugins/pendo';
import { DataDog } from '@agyletime/vue-3-common/src/plugins/datadog';
import { ClickOutsideDirective } from '@agyletime/vue-3-common/src/plugins/clickOutside';
import { BasePathPlugin } from '@agyletime/vue-3-common/src/plugins/basePath';

// Error state (from old)
import { showNavigationError } from '../store/errorState';

// MSW mock service worker (for local dev)
import { startMSW } from '../mocks/browser';

/* AG Grid License */
LicenseManager.setLicenseKey(
    'CompanyName=Agyle Time,LicensedApplication=Agyle Time,LicenseType=SingleApplication,' +
    'LicensedConcurrentDeveloperCount=12,LicensedProductionInstancesCount=1,' +
    'AssetReference=AG-027189,ExpiryDate=28_May_2023_[v2]_MTY4NTIyODQwMDAwMA==adf5725b8da6512ae5a848d6fdf5c481'
);

// Start MSW only in development
if (import.meta.env.MODE === 'development') {
    await startMSW();
}

//  Clear error state if ACL bypassed
if (import.meta.env.VITE_BYPASS_ACL === 'true') {
    showNavigationError.value.show = false;
}

const app = createApp(App);

// Plugins
app.use(BasePathPlugin);
app.use(VueLogger, loggingOptions);
app.use(router);
app.use(LoggedInUserStore);
app.use(FeatureFlowPlugin);
app.use(ACLPlugin);
app.use(TooltipPlugin);
app.use(TranslationsPlugin({}));
app.use(Pendo(import.meta.env.VITE_PENDO_API_KEY));
app.use(ClickOutsideDirective);

// DataDog (keep from old)
app.use(
    DataDog(
        {
            clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
            applicationId: '6c1246de-7963-4826-9bc2-677e7c6e06f7',
            site: 'datadoghq.com',
            service: 'agyle-time-web',
            sessionSampleRate: 100,
            trackUserInteractions: true,
            allowedTracingUrls: [
                /^https:\/\/api-[^.]+\.cloud\.agyletime\.io$/,
                /^https:\/\/api-[^.]+\.[^.]+\.cloud\.agyletime\.net$/,
                'https://*.cloud.agyletime.net',
                'https://*.plvx.net'
            ]
        },
        'agyletime'
    )
);

//  Dynamic global components
app.component(
    'NavBar',
    defineAsyncComponent(async () => {
        const module = await getDynamicImport('vue-3-common.es.js') as { NavBar: Component };
        return module.NavBar;
    })
);

app.component(
    'FilterBar',
    defineAsyncComponent(async () => {
    // Use latest version from new repo
        const module = await getDynamicImport('vue-3-common.es.js') as { FilterBarAlmendra: Component };
        return module.FilterBarAlmendra;
    })
);

// Load dynamic CSS from CDN
app.use(async () => {
    const hashmap = await getHashMap();
    const stylesheet = document.createElement('link');
    stylesheet.setAttribute('rel', 'stylesheet');
    stylesheet.setAttribute('type', 'text/css');
    stylesheet.setAttribute('href', `${stylesUrl}${hashmap['style.css']}`);
    document.head.appendChild(stylesheet);
});

app.mount('#app');
